import React, { useState, useEffect } from 'react';
import { useTranslation } from '../../../shared/i18n';

/**
 * CourseGroupSelector - Displays course groups for selection (single selection only)
 * Used for CodeCombat where courses are organized into difficulty groups
 */
const CourseGroupSelector = ({
  courseGroups,
  selectedGroupAlias,
  onGroupSelect,
  isLoading,
  error,
  isTestMode
}) => {
  const { t } = useTranslation();
  const [localSelectedGroup, setLocalSelectedGroup] = useState(selectedGroupAlias || null);

  useEffect(() => {
    setLocalSelectedGroup(selectedGroupAlias || null);
  }, [selectedGroupAlias]);

  const handleGroupSelect = (groupAlias) => {
    setLocalSelectedGroup(groupAlias);
    onGroupSelect(groupAlias);
  };

  const renderGroupRadio = (group) => {
    const isSelected = localSelectedGroup === group.alias;
    const courseNames = group.course_names || [];
    const groupName = t(`codecombat.course_groups.${group.alias}.name`);
    const groupDescription = t(`codecombat.course_groups.${group.alias}.description`);

    return (
      <div
        key={group.alias}
        className={`form-check border rounded p-3 mb-2 ${isSelected ? 'border-primary bg-primary bg-opacity-10' : ''}`}
      >
        <input
          type="radio"
          className="form-check-input"
          id={`group-${group.alias}`}
          name="course-group"
          value={group.alias}
          checked={isSelected}
          onChange={() => handleGroupSelect(group.alias)}
          disabled={isLoading}
          {...(isTestMode && { 'data-testid': `group-radio-${group.alias}` })}
        />
        <label className="form-check-label w-100" htmlFor={`group-${group.alias}`}>
          <div className="d-flex justify-content-between align-items-start">
            <div className="flex-grow-1">
              <div className="fw-semibold">{groupName}</div>
              {groupDescription && (
                <small className="text-muted d-block mt-1">{groupDescription}</small>
              )}
              {courseNames.length > 0 && (
                <div className="mt-2">
                  <small className="text-secondary">
                    <strong>{t('codecombat.course_groups.courses_label')}:</strong> {courseNames.join(', ')}
                  </small>
                </div>
              )}
            </div>
            <span className="badge bg-secondary ms-2">
              {t('codecombat.course_groups.courses_count', { count: courseNames.length })}
            </span>
          </div>
        </label>
      </div>
    );
  };

  if (!courseGroups || courseGroups.length === 0) {
    return (
      <div className="text-center text-muted py-3">
        <div className="spinner-border spinner-border-sm" role="status">
          <span className="visually-hidden">{t('common.loading')}</span>
        </div>
        <p className="mt-2 mb-0 small">{t('common.loading')}</p>
      </div>
    );
  }

  return (
    <div className="course-group-selector" {...(isTestMode && { 'data-testid': 'course-group-selector' })}>
      <div className="mb-4">
        <label className="form-label fw-semibold">{t('codecombat.course_groups.title')}</label>
        <small className="form-text text-muted d-block mb-3">
          {t('codecombat.course_groups.help_text')}
        </small>

        <div className="border rounded p-3">
          <div className="d-flex flex-column gap-2">
            {courseGroups.map((group) => renderGroupRadio(group))}
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div
            className="invalid-feedback d-block mt-2"
            {...(isTestMode && { 'data-testid': 'group-selection-error' })}
          >
            {error}
          </div>
        )}

        {/* Selection Info */}
        {localSelectedGroup && (
          <div className="mt-2">
            <small className="text-muted">
              {t('codecombat.course_groups.selected')}: {t(`codecombat.course_groups.${localSelectedGroup}.name`)}
            </small>
          </div>
        )}
      </div>
    </div>
  );
};

export default CourseGroupSelector;
